import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, X, ChevronLeft, ChevronRight,
  UserX, Mail, Lock, ToggleLeft, ToggleRight, History, Filter
} from 'lucide-react';
import { preferencesApi } from '../services/emailApi';
import { useUserContext, canWrite } from '../../../context/userContext';
import { useToast } from '../shared/ToastProvider';
import './EmailPreferences.css';

/* ─── Category display helpers ─────────────────── */
const CATEGORY_COLORS = {
  transactional:       'neutral',
  campaigns_marketing: 'primary',
  automation_events:   'info',
  scheduled_reminders: 'warning',
  reports_analytics:   'success',
  billing_subscription:'danger',
  product_updates:     'info',
};

const formatCategoryLabel = (key) =>
  key?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || key;

/* ═══════════════════════════════════════════════ */
/*  EmailPreferences — Admin preference management */
/* ═══════════════════════════════════════════════ */
const EmailPreferences = () => {
  const toast = useToast();
  const { userCtx } = useUserContext();
  const canWritePrefs = !userCtx || !userCtx.pages || canWrite(userCtx, 'email_preferences');

  /* ── Categories ── */
  const [categories, setCategories] = useState([]);
  const [, setCategoriesLoading] = useState(true);

  /* ── Stats ── */
  const [stats, setStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);

  /* ── Unsubscribed table ── */
  const [unsubs, setUnsubs] = useState([]);
  const [unsubsLoading, setUnsubsLoading] = useState(false);
  const [unsubsTotal, setUnsubsTotal] = useState(0);
  const [unsubsPage, setUnsubsPage] = useState(1);
  const [unsubsFilter, setUnsubsFilter] = useState('');
  const LIMIT = 50;

  /* ── Detail drawer ── */
  const [drawerEmail, setDrawerEmail] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerPrefs, setDrawerPrefs] = useState({});
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [drawerHistory, setDrawerHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  /* ── Refreshing ── */
  const [refreshing, setRefreshing] = useState(false);

  /* ─── Fetch categories ─────────────────── */
  useEffect(() => {
    preferencesApi.categories()
      .then((res) => {
        const d = res?.data?.data ?? res?.data ?? [];
        if (Array.isArray(d)) setCategories(d);
      })
      .catch(() => {})
      .finally(() => setCategoriesLoading(false));
  }, []);

  /* ─── Fetch stats ──────────────────────── */
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await preferencesApi.stats();
      const d = res?.data?.data ?? res?.data ?? [];
      if (Array.isArray(d)) setStats(d);
    } catch {
      /* silent */
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  /* ─── Fetch unsubscribed list ──────────── */
  const fetchUnsubs = useCallback(async () => {
    setUnsubsLoading(true);
    try {
      const params = { page: unsubsPage, limit: LIMIT };
      if (unsubsFilter) params.category = unsubsFilter;
      const res = await preferencesApi.list(params);
      const body = res?.data;
      setUnsubs(body?.data ?? []);
      setUnsubsTotal(body?.total ?? 0);
    } catch {
      toast?.error?.('Failed to load unsubscribed list');
    } finally {
      setUnsubsLoading(false);
    }
  }, [unsubsPage, unsubsFilter, toast]);

  useEffect(() => { fetchUnsubs(); }, [fetchUnsubs]);

  /* ─── Refresh all ──────────────────────── */
  const handleRefresh = () => {
    setRefreshing(true);
    Promise.all([fetchStats(), fetchUnsubs()])
      .finally(() => setRefreshing(false));
  };

  /* ─── Open detail drawer ───────────────── */
  const openDrawer = async (email) => {
    setDrawerEmail(email);
    setDrawerLoading(true);
    setDrawerData(null);
    setDrawerHistory([]);
    setHistoryOpen(false);
    try {
      const res = await preferencesApi.getByEmail(email);
      const body = res?.data;
      const prefs = body?.data ?? [];
      setDrawerData(prefs);
      // Build editable state
      const prefsMap = {};
      prefs.forEach((p) => { prefsMap[p.category] = p.is_subscribed !== false; });
      setDrawerPrefs(prefsMap);
    } catch {
      toast?.error?.('Failed to load preferences');
      setDrawerEmail(null);
    } finally {
      setDrawerLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerEmail(null);
    setDrawerData(null);
    setDrawerPrefs({});
    setDrawerHistory([]);
    setHistoryOpen(false);
  };

  /* ─── Toggle a preference ──────────────── */
  const togglePref = (category) => {
    setDrawerPrefs((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  /* ─── Save preferences ─────────────────── */
  const savePref = async () => {
    if (!drawerEmail) return;
    setDrawerSaving(true);
    try {
      // Exclude transactional (locked)
      const payload = {};
      Object.entries(drawerPrefs).forEach(([cat, val]) => {
        if (cat !== 'transactional') payload[cat] = val;
      });
      await preferencesApi.updateByEmail(drawerEmail, payload);
      toast?.success?.('Preferences updated');
      fetchUnsubs();
      fetchStats();
    } catch {
      toast?.error?.('Failed to save preferences');
    } finally {
      setDrawerSaving(false);
    }
  };

  /* ─── Force resubscribe single category ── */
  const forceResub = async (email, category) => {
    try {
      await preferencesApi.resubscribe(email, category);
      toast?.success?.(`Resubscribed ${email} to ${formatCategoryLabel(category)}`);
      // Refresh drawer
      if (drawerEmail === email) openDrawer(email);
      fetchUnsubs();
      fetchStats();
    } catch {
      toast?.error?.('Failed to resubscribe');
    }
  };

  /* ─── Fetch audit history ──────────────── */
  const fetchHistory = async () => {
    if (!drawerEmail) return;
    setHistoryLoading(true);
    try {
      const res = await preferencesApi.history(drawerEmail);
      setDrawerHistory(res?.data?.data ?? []);
      setHistoryOpen(true);
    } catch {
      toast?.error?.('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const totalPages = Math.ceil(unsubsTotal / LIMIT) || 1;
  const totalUnsubs = stats.reduce((sum, s) => sum + parseInt(s.count || 0, 10), 0);

  /* ═══════════════════════════════════════════ */
  /*  RENDER                                     */
  /* ═══════════════════════════════════════════ */
  return (
    <div className="empref-page">
      {/* Header */}
      <div className="empref-header">
        <div>
          <h1 className="empref-title">Email Preferences</h1>
          <p className="empref-subtitle">
            Manage subscriber opt-out preferences across categories
          </p>
        </div>
        <button
          className={`em-btn em-btn-secondary ${refreshing ? 'spinning' : ''}`}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          <RefreshCw size={16} className={refreshing ? 'spin-icon' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* ─── Stats Cards ── */}
      <div className="empref-stats-row">
        {statsLoading ? (
          [0, 1, 2, 3].map((i) => (
            <div key={i} className="empref-stat-card">
              <div className="em-skl" style={{ width: '60%', height: 24 }} />
              <div className="em-skl" style={{ width: '40%', height: 14, marginTop: 6 }} />
            </div>
          ))
        ) : (
          <>
            {/* Total card */}
            <div className="empref-stat-card empref-stat-total">
              <div className="empref-stat-icon"><UserX size={20} /></div>
              <div>
                <h3 className="empref-stat-value">{totalUnsubs}</h3>
                <p className="empref-stat-label">Total Unsubscribes</p>
              </div>
            </div>
            {/* Per-category cards */}
            {stats.map((s) => (
              <div key={s.category} className={`empref-stat-card empref-stat-${CATEGORY_COLORS[s.category] || 'neutral'}`}>
                <div>
                  <h3 className="empref-stat-value">{parseInt(s.count || 0, 10).toLocaleString()}</h3>
                  <p className="empref-stat-label">{formatCategoryLabel(s.category)}</p>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* ─── Unsubscribed Table ── */}
      <div className="em-card empref-table-card">
        <div className="empref-table-header">
          <div>
            <h2 className="em-card-title">Unsubscribed Entries</h2>
            <p className="em-card-sub">{unsubsTotal} total entries</p>
          </div>
          <div className="empref-table-filters">
            <div className="empref-filter-select">
              <Filter size={14} />
              <select
                className="em-select em-select-sm"
                value={unsubsFilter}
                onChange={(e) => { setUnsubsFilter(e.target.value); setUnsubsPage(1); }}
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c.key || c} value={c.key || c}>
                    {c.label || formatCategoryLabel(c.key || c)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="empref-table-wrap">
          {unsubsLoading ? (
            <div className="empref-table-loading">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="empref-table-skel">
                  <div className="em-skl" style={{ width: '40%', height: 16 }} />
                  <div className="em-skl" style={{ width: '25%', height: 16 }} />
                  <div className="em-skl" style={{ width: '20%', height: 16 }} />
                </div>
              ))}
            </div>
          ) : unsubs.length === 0 ? (
            <div className="empref-empty">
              <Mail size={24} />
              <p>No unsubscribed entries{unsubsFilter ? ` for ${formatCategoryLabel(unsubsFilter)}` : ''}.</p>
            </div>
          ) : (
            <table className="empref-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Category</th>
                  <th>Unsubscribed At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {unsubs.map((u, i) => (
                  <tr key={i}>
                    <td>
                      <button className="empref-email-btn" onClick={() => openDrawer(u.email_address)}>
                        {u.email_address}
                      </button>
                    </td>
                    <td>
                      <span className={`em-badge em-badge-${CATEGORY_COLORS[u.category] || 'neutral'}`}>
                        {formatCategoryLabel(u.category)}
                      </span>
                    </td>
                    <td className="empref-date">
                      {u.updated_at ? new Date(u.updated_at).toLocaleString() : '—'}
                    </td>
                    <td>
                      <button
                        className="em-btn em-btn-ghost em-btn-xs"
                        onClick={() => forceResub(u.email_address, u.category)}
                        title="Re-subscribe"
                        disabled={!canWritePrefs}
                      >
                        Re-subscribe
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="empref-pagination">
            <button
              className="em-btn em-btn-ghost em-btn-sm"
              disabled={unsubsPage <= 1}
              onClick={() => setUnsubsPage((p) => p - 1)}
            >
              <ChevronLeft size={14} /> Prev
            </button>
            <span className="empref-page-info">
              Page {unsubsPage} of {totalPages}
            </span>
            <button
              className="em-btn em-btn-ghost em-btn-sm"
              disabled={unsubsPage >= totalPages}
              onClick={() => setUnsubsPage((p) => p + 1)}
            >
              Next <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* ─── Detail Drawer Overlay ── */}
      {drawerEmail && (
        <div className="empref-drawer-overlay" onClick={closeDrawer}>
          <div className="empref-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="empref-drawer-header">
              <div>
                <h3 className="empref-drawer-title">Preferences</h3>
                <p className="empref-drawer-email">{drawerEmail}</p>
              </div>
              <button className="empref-drawer-close" onClick={closeDrawer}>
                <X size={18} />
              </button>
            </div>

            <div className="empref-drawer-body">
              {drawerLoading ? (
                <div className="empref-drawer-loading">
                  {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="empref-drawer-skel">
                      <div className="em-skl" style={{ width: '50%', height: 18 }} />
                      <div className="em-skl" style={{ width: 44, height: 24, borderRadius: 12 }} />
                    </div>
                  ))}
                </div>
              ) : drawerData ? (
                <>
                  <div className="empref-prefs-list">
                    {(drawerData || []).map((p) => {
                      const isLocked = p.locked === true;
                      const isSubscribed = drawerPrefs[p.category] !== false;
                      return (
                        <div key={p.category} className={`empref-pref-row ${isLocked ? 'locked' : ''}`}>
                          <div className="empref-pref-info">
                            <div className="empref-pref-name">
                              {p.label || formatCategoryLabel(p.category)}
                              {isLocked && (
                                <span className="empref-lock-badge">
                                  <Lock size={10} /> Required
                                </span>
                              )}
                            </div>
                            <p className="empref-pref-desc">{p.description || ''}</p>
                          </div>
                          <button
                            className={`empref-toggle ${isSubscribed ? 'on' : 'off'}`}
                            disabled={isLocked || !canWritePrefs}
                            onClick={() => !isLocked && canWritePrefs && togglePref(p.category)}
                            title={isLocked ? 'This category cannot be unsubscribed' : (isSubscribed ? 'Click to unsubscribe' : 'Click to subscribe')}
                          >
                            {isSubscribed ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <div className="empref-drawer-actions">
                    <button
                      className="em-btn em-btn-primary"
                      onClick={savePref}
                      disabled={drawerSaving || !canWritePrefs}
                    >
                      {drawerSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      className="em-btn em-btn-secondary"
                      onClick={fetchHistory}
                      disabled={historyLoading}
                    >
                      <History size={14} />
                      {historyLoading ? 'Loading...' : 'Audit History'}
                    </button>
                  </div>

                  {/* History section */}
                  {historyOpen && (
                    <div className="empref-history">
                      <h4 className="empref-history-title">Audit History</h4>
                      {drawerHistory.length === 0 ? (
                        <p className="em-text-sm em-text-muted">No history found.</p>
                      ) : (
                        <div className="empref-history-list">
                          {drawerHistory.map((h, i) => (
                            <div key={h.id || i} className="empref-history-item">
                              <div className="empref-history-meta">
                                <span className={`em-badge em-badge-xs em-badge-${CATEGORY_COLORS[h.category] || 'neutral'}`}>
                                  {formatCategoryLabel(h.category)}
                                </span>
                                <span className="empref-history-date">
                                  {h.unsubscribed_at ? new Date(h.unsubscribed_at).toLocaleString() : h.created_at ? new Date(h.created_at).toLocaleString() : '—'}
                                </span>
                              </div>
                              <p className="empref-history-reason">{h.reason || 'No reason recorded'}</p>
                              {h.campaign_id && (
                                <span className="em-text-xs em-text-muted">Campaign #{h.campaign_id}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailPreferences;
